import {
  Button,
  Checkbox,
  Group,
  Loader,
  MultiSelect,
  NumberInput,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { rawApi } from '../../api/client';
import { listKey } from '../../api/queryKeys';
import { selectData } from '../../components/EntityField';
import { EmptyNote, RecordScreen } from '../../components/RecordScreen';
import { useEntityIndex } from '../../hooks/useEntityIndex';
import { useBaseCurrency } from '../../hooks/useSeedLocation';
import { enumLabel, enumOptionsFor } from '../../lib/enums';
import { currencySymbol, toMajorUnits, toMinorUnits } from '../../lib/format';
import { notifyError, notifySaved } from '../../lib/notify';
import {
  bySurface,
  onlyStaffSurfaces,
  PERMISSIONS_PATH,
  type Permission,
  ROLES_PATH,
  type RoleGrant,
  rolePermissionsPath,
} from './permissions';
import { RoleHoldersPanel } from './RoleHoldersPanel';

interface Role {
  id: string;
  name: string;
  description?: string | null;
  seeded?: boolean;
}

/**
 * The places a capability can be pinned to.
 *
 * Empty means every one of them, which is why the placeholder says so rather than inviting a
 * choice: a manager who reads "Choose any area" over an empty box will assume the grant is doing
 * nothing until they pick something, and will scope a role that was already correct.
 */
function ScopePicker({
  scopeKind,
  value,
  onChange,
  disabled,
}: {
  scopeKind: string;
  value: string[];
  onChange: (next: string[]) => void;
  disabled?: boolean;
}) {
  const areas = useEntityIndex('storageLocation');

  if (scopeKind === 'storageLocation') {
    return (
      // Under room headings, the same as every other area picker. There is a "Deep freezer" in all
      // three rooms; a flat list of identical names is a coin toss, and the coin decides which
      // room a bartender may write stock off from. The summary on the roles list already reads
      // "The Bodega — Deep freezer", so a flat box here also disagreed with the sentence it wrote.
      <MultiSelect
        size="xs"
        w={260}
        data={selectData(areas.options)}
        placeholder={value.length > 0 ? undefined : 'Everywhere stock is kept'}
        value={value}
        onChange={onChange}
        disabled={disabled || areas.isLoading}
        error={areas.isError ? 'Areas could not be loaded' : undefined}
        clearable
        comboboxProps={{ withinPortal: true }}
      />
    );
  }

  if (scopeKind === 'kdsStation') {
    // A station is a fixed part of the menu — where a dish is made — not a row anybody created,
    // so its names come from the shared wording rather than a lookup.
    return (
      <MultiSelect
        size="xs"
        w={260}
        data={enumOptionsFor('prepStation', ['kitchen', 'bar'])}
        placeholder={value.length > 0 ? undefined : 'Every screen'}
        value={value}
        onChange={onChange}
        disabled={disabled}
        clearable
        comboboxProps={{ withinPortal: true }}
      />
    );
  }

  // A kind with nothing to pick from yet. Drawing an empty box would ask a question that has no
  // answers, and the grant already means "all of them" — which is what leaving it alone does.
  return null;
}

/**
 * The most a capability may give away in one go.
 *
 * Held locally while it is being typed and sent on the way out, because saving each keystroke
 * would send "L 5" on the way to "L 500" and briefly make it true.
 */
function LimitInput({
  value,
  currency,
  disabled,
  onCommit,
}: {
  value: number | null;
  currency: string;
  disabled?: boolean;
  onCommit: (limitMinor: number | null) => void;
}) {
  const [draft, setDraft] = useState<number | string>(toMajorUnits(value));

  // Re-seed when the saved figure changes underneath — another tab, or a save that was refused.
  useEffect(() => setDraft(toMajorUnits(value)), [value]);

  return (
    <Group gap={6} wrap="nowrap">
      <Text size="xs" c="dimmed">
        Up to
      </Text>
      <NumberInput
        size="xs"
        w={150}
        value={draft}
        onChange={setDraft}
        onBlur={() => {
          const next = draft === '' || draft === null ? null : (toMinorUnits(draft) ?? null);
          if (next !== value) onCommit(next);
        }}
        prefix={`${currencySymbol(currency)} `}
        decimalScale={2}
        thousandSeparator=","
        hideControls
        placeholder="No limit"
        disabled={disabled}
      />
    </Group>
  );
}

/**
 * One thing a person can do, with the two ways they can be trusted with it kept apart.
 *
 * Doing it and waving somebody else through are separate grants that never imply one another, and
 * collapsing them into a single tick would mean the only way to let a shift lead approve a comp is
 * to let every server hand one out unasked. That is the mistake this screen exists to prevent, so
 * the two sit on their own lines with their own caps and their own places.
 */
function CapabilityRow({
  heading,
  modes,
  grants,
  currency,
  disabled,
  onGrant,
  onRevoke,
}: {
  heading: string;
  modes: Permission[];
  grants: Map<string, RoleGrant>;
  currency: string;
  disabled: boolean;
  onGrant: (permission: Permission, patch: { scopeIds: string[]; limitMinor: number | null }) => void;
  onRevoke: (permission: Permission) => void;
}) {
  return (
    <Stack gap={4}>
      <Text size="sm" fw={600}>
        {heading}
      </Text>
      {modes.map((permission) => {
        const grant = grants.get(permission.id);
        return (
          <Group key={permission.id} gap="md" wrap="wrap" align="center" pl="md">
            <Checkbox
              size="sm"
              label={enumLabel('permissionMode', permission.mode)}
              checked={Boolean(grant)}
              disabled={disabled}
              onChange={(event) =>
                event.currentTarget.checked
                  ? onGrant(permission, { scopeIds: [], limitMinor: null })
                  : onRevoke(permission)
              }
            />
            {grant && permission.takesLimit && (
              <LimitInput
                value={grant.limitMinor}
                currency={currency}
                disabled={disabled}
                onCommit={(limitMinor) => onGrant(permission, { scopeIds: grant.scopeIds, limitMinor })}
              />
            )}
            {grant && permission.scopeKind && (
              <ScopePicker
                scopeKind={permission.scopeKind}
                value={grant.scopeIds}
                disabled={disabled}
                onChange={(scopeIds) => onGrant(permission, { scopeIds, limitMinor: grant.limitMinor })}
              />
            )}
          </Group>
        );
      })}
    </Stack>
  );
}

/** The role's own name and note, changed here rather than back on the list it came from. */
function RoleAboutPanel({ role }: { role: Role }) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);

  const form = useForm({
    initialValues: { name: role.name, description: role.description ?? '' },
    validate: { name: (v) => (v.trim() ? null : 'A name is required') },
  });

  const save = useMutation({
    mutationFn: (values: typeof form.values) =>
      rawApi('PATCH', `${ROLES_PATH}/${role.id}`, {
        name: values.name.trim(),
        description: values.description.trim() || null,
      }),
    onSuccess: (_data, values) => {
      void queryClient.invalidateQueries({ queryKey: listKey(ROLES_PATH) });
      setEditing(false);
      notifySaved(values.name.trim());
    },
    onError: (err) => notifyError(err, 'save this role'),
  });

  if (!editing) {
    return (
      <Group justify="space-between" align="flex-start" wrap="nowrap">
        <Text
          size="sm"
          c={role.description ? undefined : 'dimmed'}
          fs={role.description ? undefined : 'italic'}
        >
          {role.description || 'No description yet.'}
        </Text>
        <Button size="sm" variant="light" onClick={() => setEditing(true)}>
          Rename
        </Button>
      </Group>
    );
  }

  return (
    <form onSubmit={form.onSubmit((values) => save.mutate(values))}>
      <Stack gap="md">
        <TextInput
          label="Name"
          description="What the job is called — Bartender, Kitchen, Shift Lead"
          required
          {...form.getInputProps('name')}
        />
        <TextInput
          label="Description"
          description="What the job covers, in your own words"
          {...form.getInputProps('description')}
        />
        <Group justify="flex-end">
          <Button
            variant="default"
            onClick={() => {
              form.reset();
              setEditing(false);
            }}
            disabled={save.isPending}
          >
            Cancel
          </Button>
          <Button type="submit" loading={save.isPending}>
            Save
          </Button>
        </Group>
      </Stack>
    </form>
  );
}

/**
 * A role, as the thing it actually is: a list of what somebody holding it may do at a register.
 *
 * Until this page existed a role was a name and a sentence in a table, and the permission matrix
 * behind it could only be reached with an API client — which meant in practice that it was never
 * touched, and every role granted exactly nothing. Naming somebody "Shift Lead" changed what the
 * register would let them do by zero.
 *
 * Every change saves on the spot, one capability at a time. The alternative — a Save button over
 * the whole matrix — sends the entire set back on every tick, so two managers editing two roles in
 * two tabs each quietly undo the other's work, and neither is told.
 */
export function RoleDetailPage() {
  const { roleId = '' } = useParams();
  const queryClient = useQueryClient();
  const currency = useBaseCurrency();

  const rolesQuery = useQuery({
    queryKey: listKey(ROLES_PATH),
    queryFn: () => rawApi<Role[]>('GET', ROLES_PATH),
  });
  const role = (rolesQuery.data ?? []).find((r) => r.id === roleId);

  const catalogQuery = useQuery({
    queryKey: listKey(PERMISSIONS_PATH),
    queryFn: () => rawApi<Permission[]>('GET', PERMISSIONS_PATH),
  });

  const grantsQuery = useQuery({
    queryKey: listKey(rolePermissionsPath(roleId)),
    queryFn: () => rawApi<RoleGrant[]>('GET', rolePermissionsPath(roleId)),
    enabled: Boolean(roleId),
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: listKey(rolePermissionsPath(roleId)) });
    /*
     * Anybody holding this role now does something different at the register, and their page says so
     * in a sentence worked out by the API. Left in cache that sentence keeps describing the role
     * as it was before this tick — the reader's own page contradicting the change they just made.
     * Matched by suffix rather than by name because it is every person's answer that went stale,
     * not one we happen to know the id of.
     */
    void queryClient.invalidateQueries({
      predicate: (query) =>
        typeof query.queryKey[1] === 'string' && query.queryKey[1].endsWith('/capabilities'),
    });
  };

  const grant = useMutation({
    mutationFn: (input: { permission: Permission; scopeIds: string[]; limitMinor: number | null }) =>
      rawApi('PUT', `${rolePermissionsPath(roleId)}/${input.permission.id}`, {
        scopeIds: input.scopeIds,
        limitMinor: input.limitMinor,
      }),
    onSuccess: refresh,
    onError: (err) => notifyError(err, 'change what this role can do'),
  });

  const revoke = useMutation({
    mutationFn: (permission: Permission) =>
      rawApi('DELETE', `${rolePermissionsPath(roleId)}/${permission.id}`),
    onSuccess: refresh,
    onError: (err) => notifyError(err, 'change what this role can do'),
  });

  const saving = grant.isPending || revoke.isPending;
  const catalog = onlyStaffSurfaces(catalogQuery.data ?? []);
  const grants = new Map((grantsQuery.data ?? []).map((g) => [g.permissionId, g]));
  const granted = catalog.filter((p) => grants.has(p.id));

  /*
   * What this role is allowed to do is only knowable once ALL THREE have arrived, and the grants
   * are the reason. An unanswered grants request leaves that map empty, which is indistinguishable
   * from a role that has been given nothing — so the page would draw fifty capabilities with every
   * box unticked and a header reading "Can do 0 things", and the owner would either believe his
   * Shift Lead had been stripped or start ticking boxes back on. The error has to replace the
   * matrix, not sit underneath it.
   *
   * An error with an answer still cached is only a failed REFRESH: what is on screen was true a
   * moment ago, so it stays, rather than throwing away a correct page over one dropped request.
   */
  const loading = rolesQuery.isLoading || catalogQuery.isLoading || grantsQuery.isLoading;
  const loadFailed =
    (rolesQuery.isError && !rolesQuery.data) ||
    (catalogQuery.isError && !catalogQuery.data) ||
    (grantsQuery.isError && !grantsQuery.data);
  const ready = Boolean(role) && !loadFailed;

  return (
    <RecordScreen
      // The tab is named in the address because "All roles" landed on the list of PEOPLE: the
      // staff screen opens on its first tab, and a link that promises one list and delivers
      // another is read as the app having lost the roles.
      backTo="/staff?tab=roles"
      backLabel="All roles"
      isLoading={loading}
      loadFailed={!loading && loadFailed}
      loadFailedMessage="What this role can do could not be read just now. Nothing has changed. Refresh the page to try again."
      // Only once the roles list itself actually arrived: a failed request naming a role that is
      // sitting safely in the database must not be reported as a role somebody deleted.
      notFound={!loading && !loadFailed && !role}
      notFoundMessage="This role is no longer on file."
      title={role?.name ?? ''}
      /*
       * Said in the future tense on purpose, and said here because this page is reached straight
       * from a badge on somebody's record — the reader may never have passed the same sentence on
       * the Roles tab. Nothing on this screen locks anything: a register reads these when someone types
       * their PIN, and a ticked box is what that register will allow, not a door being shut now. The
       * second half is the one that must not be droppable — every checkbox here is about a register,
       * and none of it hands anybody a way into this site.
       */
      subtitle={
        ready
          ? 'What this job will let someone do at a register, the kitchen screen and the stock count once they have typed their PIN. It gives nobody access to this site.'
          : undefined
      }
      status={role?.seeded ? { label: 'Built in', color: 'gray' } : undefined}
      actions={
        saving ? (
          <Group gap="xs">
            <Loader size="xs" />
            <Text size="xs" c="dimmed">
              Saving…
            </Text>
          </Group>
        ) : undefined
      }
      facts={
        ready
          ? [
              {
                label: 'Can do',
                value: `${granted.filter((p) => p.mode === 'perform').length} things`,
              },
              {
                label: 'Can approve',
                value: `${granted.filter((p) => p.mode === 'approve').length} things`,
              },
            ]
          : []
      }
      sections={
        ready && role
          ? [
              { title: 'About this role', children: <RoleAboutPanel role={role} /> },
              {
                // Above the capability matrix, because it is the shorter question and the one a
                // manager arrives with. Fifty checkboxes about what a Shift Lead may do are worth
                // reading only once you know who is one.
                title: 'Who does this job',
                description:
                  'Everyone who holds this role. Giving it to somebody changes nothing else they do.',
                children: <RoleHoldersPanel roleId={role.id} roleName={role.name} />,
              },
              ...bySurface(catalog).map(([surface, rows]) => ({
                title: enumLabel('permissionSurface', surface),
                children: (
                  <Stack gap="md">
                    {groupByAction(rows).map(([action, modes]) => (
                      <CapabilityRow
                        key={action}
                        heading={headingFor(modes)}
                        modes={modes}
                        grants={grants}
                        currency={currency}
                        disabled={saving || grantsQuery.isLoading}
                        onGrant={(permission, patch) => grant.mutate({ permission, ...patch })}
                        onRevoke={(permission) => revoke.mutate(permission)}
                      />
                    ))}
                  </Stack>
                ),
              })),
            ]
          : []
      }
    >
      {/*
        Only ever reached once the catalog genuinely arrived and genuinely was empty — the failed
        request now replaces the whole screen above, rather than adding a line under a matrix that
        has already told the reader this role can do nothing.
      */}
      {ready && catalog.length === 0 ? (
        <EmptyNote>There is nothing a role can be given yet.</EmptyNote>
      ) : null}
    </RecordScreen>
  );
}

/**
 * The two lines of one capability, kept together and in a fixed order so "can do it" is always
 * above "can approve it" rather than wherever the API happened to list them.
 */
function groupByAction(rows: readonly Permission[]): [string, Permission[]][] {
  const groups = new Map<string, Permission[]>();
  for (const row of rows) {
    const existing = groups.get(row.action);
    if (existing) existing.push(row);
    else groups.set(row.action, [row]);
  }
  return [...groups.entries()].map(([action, modes]) => [
    action,
    [...modes].sort((a, b) => (a.mode === b.mode ? 0 : a.mode === 'perform' ? -1 : 1)),
  ]);
}

/**
 * What the capability is called on screen — the API's own words for doing the thing.
 *
 * The approve row's wording is only used when nothing can be done without approval, because
 * otherwise the heading would read "Approve giving something away free" over a tick that lets
 * somebody give it away themselves.
 */
function headingFor(modes: readonly Permission[]): string {
  const perform = modes.find((m) => m.mode === 'perform') ?? modes[0];
  return perform?.description ?? '';
}
